import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster"
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import PageNotFound from './lib/PageNotFound';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
import UserNotRegisteredError from '@/components/UserNotRegisteredError';
import ScrollToTop from './components/ScrollToTop';
// Add page imports here
import Home from '@/pages/Home';
const Article = lazy(() => import('@/pages/Article'));
const About = lazy(() => import('@/pages/About'));
const Contact = lazy(() => import('@/pages/Contact'));
const BedBugTreatmentVancouver = lazy(() => import('@/pages/BedBugTreatmentVancouver'));
const ServiceAreaPage = lazy(() => import('@/pages/ServiceAreaPage'));
const ServiceAreasHub = lazy(() => import('@/pages/ServiceAreasHub'));
const NeighborhoodPage = lazy(() => import('@/pages/NeighborhoodPage'));
const PropertyTypePage = lazy(() => import('@/pages/PropertyTypePage'));
const BedBugGuideIndex = lazy(() => import('@/pages/BedBugGuideIndex'));
const SearchConsole = lazy(() => import('@/pages/SearchConsole'));

const RouteFallback = () => (
  <div className="fixed inset-0 flex items-center justify-center">
    <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin" />
  </div>
);

const AuthenticatedApp = () => {
  const { isLoadingAuth, isLoadingPublicSettings, authError, navigateToLogin } = useAuth();

  // Show loading spinner while checking app public settings or auth
  if (isLoadingPublicSettings || isLoadingAuth) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin"></div>
      </div>
    );
  }

  // Handle authentication errors
  if (authError) {
    if (authError.type === 'user_not_registered') {
      return <UserNotRegisteredError />;
    } else if (authError.type === 'auth_required') {
      // Redirect to login automatically
      navigateToLogin();
      return null;
    }
  }

  // Render the main app
  return (
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      {/* Add your page Route elements here */}
      <Route path="/" element={<Home />} />
      <Route path="/about" element={<About />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/bed-bug-treatment-vancouver" element={<BedBugTreatmentVancouver />} />
      <Route path="/bed-bug-treatment-areas" element={<ServiceAreasHub />} />
      <Route path="/bed-bug-treatment/condos" element={<PropertyTypePage type="condos" />} />
      <Route path="/bed-bug-treatment/apartments" element={<PropertyTypePage type="apartments" />} />
      <Route path="/bed-bug-treatment/hotels" element={<PropertyTypePage type="hotels" />} />
      <Route path="/bed-bug-treatment/short-term-rentals" element={<PropertyTypePage type="short-term-rentals" />} />
      <Route path="/bed-bug-treatment/homes" element={<PropertyTypePage type="homes" />} />
      <Route path="/bed-bug-treatment/:slug/:hood" element={<NeighborhoodPage />} />
      <Route path="/bed-bug-treatment/:slug" element={<ServiceAreaPage />} />
      <Route path="/admin/search-console" element={<SearchConsole />} />
      <Route path="/guide" element={<BedBugGuideIndex />} />
      <Route path="/guide/:slug" element={<Article />} />
      <Route path="*" element={<PageNotFound />} />
    </Routes>
    </Suspense>
  );
};


function App() {
  useEffect(() => {
    const loadGtag = () => {
      if (typeof window === "undefined" || window.__gads_loaded) return;
      window.__gads_loaded = true;
      window.dataLayer = window.dataLayer || [];
      const inIframe = (() => { try { return window.self !== window.top; } catch { return true; } })();
      window.gtag = function gtag() {
        window.dataLayer.push(arguments);
        if (inIframe) {
          try {
            const args = Array.prototype.slice.call(arguments);
            const cmd = args[0];
            window.parent.postMessage({
              type: 'base44_gtag_event',
              event: {
                source: 'gtag',
                timestamp: new Date().toLocaleTimeString(),
                command: cmd,
                params: args.slice(1),
                type: cmd === 'event' ? (args[1] || 'event') : cmd,
              },
            }, '*');
          } catch (_e) { /* relay must not break gtag */ }
        }
      };
      const s = document.createElement('script');
      s.src = 'https://www.googletagmanager.com/gtag/js?id=AW-18399904134';
      s.async = true;
      document.head.appendChild(s);
      window.gtag('js', new Date());
      window.gtag('config', 'AW-18399904134', { send_page_view: false });
    };
    const ric = window.requestIdleCallback;
    if (ric) ric(loadGtag, { timeout: 3000 });
    else window.setTimeout(loadGtag, 2000);
  }, []);

  return (
    <AuthProvider>
      <QueryClientProvider client={queryClientInstance}>
        <Router>
          <ScrollToTop />
          <AuthenticatedApp />
        </Router>
        <Toaster />
      </QueryClientProvider>
    </AuthProvider>
  )
}

export default App